import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnInit,
  Signal,
  viewChild,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  NODE_EDIT_TAB_COMPONENT,
  NodeEditTab,
  SelectedNodeForEdit,
} from 'src/app/shared/flow-chart/flow-chart.model';
import { WebhookNodeData } from '../../../node-edit.type';
import { WebhookSelectComponent } from './components/webhook-select/webhook-select.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { WorkflowModel } from '@models/workflow.model';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { UiCopyClipboardComponent } from '@ui';
import { MatDialog } from '@angular/material/dialog';
import { WebhookDialogComponent } from './components/webhook-dialog/webhook-dialog.component';
import { MatTooltip } from '@angular/material/tooltip';
import { WorkflowWebhookModel } from '@models/workflow-webhook.model';
import { WorkflowWebhooksSelectors } from '@states/workflow-webhooks/workflow-webhooks.selector-types';

@Component({
  selector: 'edit-webhook',
  imports: [
    WebhookSelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    UiCopyClipboardComponent,
    MatTooltip,
  ],
  templateUrl: './edit-webhook.component.html',
  styleUrl: './edit-webhook.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NODE_EDIT_TAB_COMPONENT,
      useExisting: EditWebhookComponent,
    },
  ],
})
export class EditWebhookComponent implements OnInit, NodeEditTab {
  public selectedNodeForEdit = input<SelectedNodeForEdit<WebhookNodeData>>();
  public markSelectAsTouched = input<boolean>(false);
  public disableForm = input<boolean>(false);

  public selectNodeSig: Signal<WorkflowModel.NodeConfigDocument> =
    this.store$.selectSignal(WorkflowsSelectors.selectNode);
  public selectWebhooksSig: Signal<
    WorkflowWebhookModel.WebhookEndpointMongoDocument[]
  > = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectWorkflowWebhooks,
  );

  public form: UntypedFormGroup;

  public webhookSelectSig = viewChild(WebhookSelectComponent);

  public disabledWebhooksMapSig: Signal<Map<string, boolean>> = computed(() => {
    const webhooks = this.selectWebhooksSig();

    const map = new Map();

    if (webhooks) {
      const nodeId = this.selectNodeSig()?.nodeId;

      for (const webhook of webhooks) {
        const shouldBeDisable = webhook.target.graphId && webhook.target.nodeId !== nodeId;
        map.set(webhook._id, shouldBeDisable)
      }
    }

    return map;
  })

  constructor(
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
    private store$: Store,
    private dialog: MatDialog,
  ) { }

  public ngOnInit(): void {
    this.form = this.fb.group({
      webhookId: [
        this.selectedNodeForEdit()?.data?.webhookId ?? '',
        [Validators.required],
      ],
      name: [
        {
          value: this.selectedNodeForEdit()?.data?.name ??
            this.translateService.instant('FLOW_CHART.NODES.WEBHOOK.NAME'),
          disabled: this.disableForm(),
        },
        [Validators.required],
      ],
    });

    if (this.markSelectAsTouched()) {
      this.webhookSelectSig()?.markTouched();
    }
  }

  public edit() {
    const id = this.selectNodeSig().preview.options['webhookId'];

    this.dialog.open(WebhookDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '760px',
      maxHeight: '80vh',
      data: {
        webhookId: id,
      },
    });
  }

  public onWebhookIdChange(webhookId: string): void {
    this.form.controls['webhookId'].setValue(webhookId);
  }
}
